import {
  type ModerateMessage,
  ModerationRejectedSchema,
  ModerationResultSchema,
  MutedEventSchema,
  ReportRejectedSchema,
  ReportResultSchema,
} from '@shefira/shared';

import {
  MODERATION_REFUSAL_TEXT,
  moderationResultText,
  mutedText,
  REPORT_REFUSAL_TEXT,
} from './moderation-text.js';
import { ReportPrompt } from './report-prompt.js';

/** The little of a Colyseus room this needs. */
interface ModerationRoom {
  send: (type: string, payload?: unknown) => void;
  onMessage: (type: string, callback: (payload: unknown) => void) => unknown;
}

/** The little of the chat this needs. */
interface ModerationChat {
  notice: (text: string) => void;
}

/**
 * The `moderate` variants of a chat command (`ui/chat-commands.ts`), as the
 * parser hands them over: the same shape, without the kind.
 */
export type ModerateCommand =
  | { action: 'mute'; name: string; minutes?: number; reason?: string }
  | { action: 'unmute' | 'unban'; name: string }
  | { action: 'kick'; name: string; reason?: string }
  | { action: 'ban'; name: string; days?: number; reason?: string };

/**
 * The client's side of moderation (ADR 0037): turns `/mute`, `/kick`,
 * `/ban`… and « Signaler » into intents, and tells the chat what the server
 * answered. It decides nothing: an ordinary player's `/kick` reaches the
 * server and comes back `not-admin`.
 */
export class ModerationClient {
  private readonly prompt = new ReportPrompt();

  constructor(
    private readonly room: ModerationRoom,
    chat: ModerationChat,
  ) {
    room.onMessage('moderation_result', (payload) => {
      const parsed = ModerationResultSchema.safeParse(payload);
      if (parsed.success) chat.notice(moderationResultText(parsed.data));
    });
    room.onMessage('moderation_rejected', (payload) => {
      const parsed = ModerationRejectedSchema.safeParse(payload);
      if (parsed.success) chat.notice(MODERATION_REFUSAL_TEXT[parsed.data.reason]);
    });
    room.onMessage('report_result', (payload) => {
      const parsed = ReportResultSchema.safeParse(payload);
      if (parsed.success)
        chat.notice(`Merci, ton signalement de ${parsed.data.name} est transmis.`);
    });
    room.onMessage('report_rejected', (payload) => {
      const parsed = ReportRejectedSchema.safeParse(payload);
      if (parsed.success) chat.notice(REPORT_REFUSAL_TEXT[parsed.data.reason]);
    });
    room.onMessage('muted', (payload) => {
      const parsed = MutedEventSchema.safeParse(payload);
      if (parsed.success) chat.notice(mutedText(parsed.data.until));
    });
  }

  /** A `/mute`, `/unmute`, `/kick`, `/ban` or `/unban` line: sent as the server expects it. */
  moderate(command: ModerateCommand): void {
    this.room.send('moderate', toIntent(command));
  }

  /** « Signaler » on a player: ask why, then send. */
  report(name: string): void {
    this.prompt.open(name, (reason, details) => {
      this.room.send('report', {
        name,
        reason,
        ...(details.length > 0 && { details }),
      });
    });
  }

  destroy(): void {
    this.prompt.close();
  }
}

/** Only the fields the schema knows, and none left `undefined`: it is strict. */
function toIntent(command: ModerateCommand): ModerateMessage {
  switch (command.action) {
    case 'mute':
      return {
        action: 'mute',
        name: command.name,
        minutes: command.minutes ?? 10,
        ...(command.reason !== undefined && { reason: command.reason }),
      };
    case 'unmute':
    case 'unban':
      return { action: command.action, name: command.name };
    case 'kick':
      return {
        action: 'kick',
        name: command.name,
        ...(command.reason !== undefined && { reason: command.reason }),
      };
    case 'ban':
      return {
        action: 'ban',
        name: command.name,
        ...(command.days !== undefined && { days: command.days }),
        ...(command.reason !== undefined && { reason: command.reason }),
      };
  }
}
